import { UserCircleIcon } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar"
import type { ComponentProps } from "react"
import {
	UserHoverCard,
	type UserHoverCardMember
} from "~/components/UserHoverCard"
import { cn, initials } from "~/lib/utils"

export function getUserPresence(lastSeenAt?: Date | string | null) {
	if (!lastSeenAt) return null
	const seenAt = lastSeenAt instanceof Date ? lastSeenAt : new Date(lastSeenAt)
	const timeSinceSeen = Date.now() - seenAt.getTime()
	if (!Number.isFinite(timeSinceSeen) || timeSinceSeen < 0) return null
	if (timeSinceSeen <= 15 * 60 * 1000) return "active"
	if (timeSinceSeen <= 6 * 60 * 60 * 1000) return "recent"
	return null
}

export function UnassignedIdentity({
	size = "md",
	withText = false
}: {
	size?: "sm" | "md" | "lg"
	withText?: boolean
}) {
	return (
		<span className="inline-flex items-center gap-2 text-text-sub">
			<span
				className={cn(
					"inline-flex shrink-0 items-center justify-center rounded-full bg-background-tertiary",
					size === "sm" ? "size-5" : size === "lg" ? "size-7" : "size-6"
				)}
			>
				<UserCircleIcon className="size-full text-icons" />
			</span>
			{withText && <span>Unassigned</span>}
		</span>
	)
}

export function EntityIdentity({
	kind,
	name,
	image,
	lastSeenAt,
	userId,
	member,
	variant = "default",
	size = "md",
	className,
	...props
}: Omit<ComponentProps<"span">, "children"> & {
	kind: "user" | "organization" | "team"
	name: string
	image?: string | null
	lastSeenAt?: Date | string | null
	userId?: string
	member?: UserHoverCardMember
	variant?: "default" | "mention" | "avatar"
	size?: "xs" | "sm" | "md" | "lg" | "xl"
	className?: string
}) {
	const avatarSize = cn(
		"shrink-0",
		size === "xs" && "size-4",
		size === "sm" && "size-5",
		size === "md" && "size-6",
		size === "lg" && "size-10",
		size === "xl" && "size-16"
	)
	const presence =
		kind === "user" ? getUserPresence(lastSeenAt ?? member?.lastSeenAt) : null
	const avatar = (
		<span
			className={cn(
				"relative inline-flex",
				kind === "user" ? "rounded-full" : "rounded-md",
				avatarSize,
				variant === "avatar" && className
			)}
			{...(variant === "avatar"
				? {
						role: "img",
						"aria-label": presence
							? `${name}, ${presence === "active" ? "Online" : "Idle"}`
							: name,
						...props
					}
				: {})}
		>
			<Avatar className={cn("size-full", kind !== "user" && "rounded-md")}>
				<AvatarImage src={image} alt="" />
				<AvatarFallback
					className={cn(
						"font-semibold",
						kind !== "user" && "rounded-md",
						size === "xs" && "text-[8px]",
						size === "sm" && "text-[10px]",
						size === "md" && "text-xs",
						size === "lg" && "text-sm",
						size === "xl" && "text-lg"
					)}
				>
					{initials(name || "?")}
				</AvatarFallback>
			</Avatar>
			{presence && (
				<svg
					viewBox="0 0 12 12"
					role="img"
					aria-label={presence === "active" ? "Online" : "Idle"}
					className="pointer-events-none absolute right-0 bottom-0 size-1/3"
				>
					<circle
						cx="6"
						cy="6"
						r="5"
						className={
							presence === "active" ? "fill-green-400" : "fill-yellow-300"
						}
						stroke="var(--background)"
						strokeWidth="2"
					/>
				</svg>
			)}
		</span>
	)
	const content =
		variant === "mention" && kind === "user" ? (
			<span
				className={cn(
					"inline rounded bg-mention px-1 font-medium text-mention-foreground box-decoration-clone",
					className
				)}
				{...props}
			>
				@{name}
			</span>
		) : variant === "avatar" ? (
			avatar
		) : (
			<span
				className={cn("inline-flex min-w-0 items-center gap-2", className)}
				{...props}
			>
				{avatar}
				<span
					className={cn(
						"min-w-0 truncate font-medium text-text-main",
						(size === "xs" || size === "sm") && "text-xs",
						size === "md" && "text-sm",
						size === "lg" && "text-sm font-semibold",
						size === "xl" && "text-2xl font-semibold"
					)}
				>
					{name}
				</span>
			</span>
		)

	return kind === "user" && (userId || member) ? (
		<UserHoverCard userId={userId} member={member}>
			{content}
		</UserHoverCard>
	) : (
		content
	)
}
